import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { InventorySnapshot } from '../shared/types';
import { api } from './api';
import { clampedDelta, patchVariationStock } from './inventory';
import { StockCommandQueue } from './stockQueue';

function operationId(): string {
  return crypto.randomUUID();
}

export function useStockController(notify: (message: string, kind?: 'success' | 'error') => void) {
  const queryClient = useQueryClient();
  const queue = useRef<StockCommandQueue | null>(null);
  if (!queue.current) queue.current = new StockCommandQueue();

  function snapshot() { return queryClient.getQueryData<InventorySnapshot>(['inventory']); }
  function store(value: InventorySnapshot) { queryClient.setQueryData(['inventory'], value); }

  function adjust(variationId: string, locationId: string | null, requestedDelta: number): boolean {
    if (!locationId) {
      notify('Choose a location before changing stock.', 'error');
      return false;
    }
    const currentSnapshot = snapshot();
    const variation = currentSnapshot?.items.flatMap((item) => item.variations).find((entry) => entry.id === variationId);
    if (!currentSnapshot || !variation) return false;
    const current = variation.stockByLocation[locationId] ?? 0;
    const delta = clampedDelta(current, requestedDelta);
    if (delta === 0) return false;
    store(patchVariationStock(currentSnapshot, variationId, locationId, (quantity) => quantity + delta));
    const key = `${variationId}:${locationId}`;
    queue.current?.enqueue(
      key,
      delta,
      (coalesced, id) => api.adjust(variationId, locationId, coalesced, id),
      (failedDelta) => {
        const latest = snapshot();
        if (latest) store(patchVariationStock(latest, variationId, locationId, (quantity) => quantity - failedDelta));
        notify('Stock change failed and was rolled back.', 'error');
        void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    );
    return true;
  }

  async function setStock(variationId: string, locationId: string | null, requested: number): Promise<boolean> {
    if (!locationId) {
      notify('Choose a location before setting stock.', 'error');
      return false;
    }
    const quantity = Math.min(99_999, Math.max(0, Math.trunc(requested)));
    const key = `${variationId}:${locationId}`;
    try {
      await queue.current?.serialize(key, async () => {
        const before = snapshot();
        if (!before) return;
        store(patchVariationStock(before, variationId, locationId, () => quantity));
        try {
          await api.setStock(variationId, locationId, quantity, operationId());
        } catch (error) {
          store(before);
          throw error;
        }
      });
      notify('Stock count saved.', 'success');
      return true;
    } catch {
      notify('Could not save that stock count. Your previous count was restored.', 'error');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      return false;
    }
  }

  async function updateItem(itemId: string, name: string, description: string): Promise<boolean> {
    const before = snapshot();
    if (!before) return false;
    const next = { ...before, items: before.items.map((item) => item.id === itemId ? { ...item, name, description } : item) };
    store(next);
    try {
      await api.updateItem(itemId, name, description, operationId());
      notify('Product details saved.', 'success');
      return true;
    } catch {
      store(before);
      notify('Could not save product details. Your previous details were restored.', 'error');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      return false;
    }
  }

  return { adjust, setStock, updateItem };
}
